import type { ICSS } from "app/UtilitiesAndConstants/types/cssTypes";
import styled from "styled-components";
import type { FC } from "react";
import { SegmentFavicon } from "pro-features/components/Chart/src/dataLabels/favicon/SegmentFavicon";
import { Favicon } from "@similarweb/ui-components";
import { rgba } from "@similarweb/styles";

const CIRCLE_POSITION_DIVIDER = 2;
const ICON_POSITION_DIVIDER = 1.7;
const calculateOffset = (length: number, unit: string, divider: number) => `-${length / divider}${unit}`;
const calculateCircleWidth = (length: number, unit: string) => `${length}${unit}`;

interface ICircle {
    color: string;
    diameter: ICSS;
    size?: string;
    isPreviousPeriod?: boolean;
}

const Wrapper = styled.div<{ offset: string; iconOffset: string }>`
    position: absolute;
    right: ${({ offset }) => offset};
    top: ${({ offset }) => offset};
    & > *:nth-child(2) {
        position: relative;
        z-index: 200;
        top: -13px;
        right: ${({ iconOffset }) => iconOffset};
    }
`;

const Circle = styled.div<ICircle>`
    width: ${({ diameter: { length, unit } }) => `${length}${unit}`};
    box-sizing: content-box;
    aspect-ratio: 1;
    border-radius: 50%;
    background-color: ${({ color }) => rgba(color, 0.12)};
    outline: ${({ isPreviousPeriod, color }) => (isPreviousPeriod ? `2px dashed ${color}` : `2px solid ${color}`)};
    outline-offset: -2px;
    z-index: ${({ isPreviousPeriod }) => (isPreviousPeriod ? 0 : 100)};
`;

interface IDataLabel extends ICircle {
    favicon: string;
    isSegment?: boolean;
    isPreviousPeriod?: boolean;
}

export const DataLabel: FC<IDataLabel> = (props) => {
    const { favicon, isSegment = false, isPreviousPeriod = false, ...circleProps } = props;
    const { diameter } = circleProps;
    const { length, unit } = diameter;
    const wrapperOffset = calculateOffset(length, unit, CIRCLE_POSITION_DIVIDER);
    const iconOffset = calculateOffset(length, unit, ICON_POSITION_DIVIDER);
    const circleWidth = calculateCircleWidth(length, unit);

    let icon = null;
    if (!isPreviousPeriod) {
        if (isSegment) {
            icon = <SegmentFavicon src={favicon} />;
        } else {
            icon = <Favicon src={favicon} />;
        }
    }

    return (
        <Wrapper
            offset={wrapperOffset}
            iconOffset={iconOffset}
        >
            <Circle
                {...circleProps}
                size={circleWidth}
                isPreviousPeriod={isPreviousPeriod}
            />
            {icon}
        </Wrapper>
    );
};
